import { useEffect, useRef } from 'react'
import { toast } from 'sonner'
import { Loader2Icon, RefreshCwIcon, WifiIcon, WifiOffIcon } from 'lucide-react'
import { useWebSocket, useWebSocketStatus } from '@/hooks/use-websocket'
import { cn } from '@/utils'
import { Button } from '@/components/ui/button'

interface ConnectionStatusBannerProps {
  /** Whether to show inline status in the UI */
  showInlineBanner?: boolean
  /** Class name for the banner */
  className?: string
}

/**
 * ConnectionStatusBanner displays the WebSocket connection status
 * Shows toast notifications for connection state changes and
 * optionally renders an inline banner for persistent status display
 */
export function ConnectionStatusBanner({
  showInlineBanner = false,
  className,
}: ConnectionStatusBannerProps) {
  const { status, isConnected, reconnectAttempts } = useWebSocketStatus()
  const { reconnect } = useWebSocket()
  const previousStatusRef = useRef(status)
  const hasShownConnectedToast = useRef(false)

  // Show toast notifications on status changes
  useEffect(() => {
    const previousStatus = previousStatusRef.current

    previousStatusRef.current = status

    // Skip initial disconnected state
    if (previousStatus === 'disconnected' && status === 'connecting') {
      return
    }

    // Show toast when connected (initial connection or after reconnecting)
    if (
      status === 'connected' &&
      (previousStatus === 'connecting' || previousStatus === 'reconnecting')
    ) {
      const isReconnect = previousStatus === 'reconnecting'

      toast.success('Connected', {
        description: isReconnect
          ? 'Connection restored successfully.'
          : 'WebSocket connection established.',
      })
      hasShownConnectedToast.current = true
    }

    // Show toast when disconnected unexpectedly (from connected state)
    if (status === 'reconnecting' && previousStatus === 'connected') {
      toast.warning('Connection Lost', {
        description: `Attempting to reconnect... (Attempt ${reconnectAttempts + 1})`,
      })
    }

    // Show toast when initial connection fails (from connecting state)
    if (status === 'reconnecting' && previousStatus === 'connecting') {
      toast.warning('Connection Failed', {
        description: `Unable to connect. Retrying... (Attempt ${reconnectAttempts + 1})`,
      })
    }

    // Show toast when max reconnect attempts reached
    if (status === 'disconnected' && previousStatus === 'reconnecting' && reconnectAttempts > 0) {
      toast.error('Connection Failed', {
        description: 'Unable to establish connection.',
        action: {
          label: 'Retry',
          onClick: reconnect,
        },
      })
    }
  }, [status, reconnectAttempts, reconnect])

  // Don't render inline banner if not requested
  if (!showInlineBanner) {
    return null
  }

  const statusConfig = {
    connecting: {
      icon: Loader2Icon,
      iconClass: 'animate-spin text-yellow-600',
      bgClass: 'bg-yellow-50 border-yellow-200 dark:bg-yellow-950 dark:border-yellow-800',
      text: 'Connecting...',
      textClass: 'text-yellow-800 dark:text-yellow-200',
    },
    connected: {
      icon: WifiIcon,
      iconClass: 'text-green-600',
      bgClass: 'bg-green-50 border-green-200 dark:bg-green-950 dark:border-green-800',
      text: 'Connected',
      textClass: 'text-green-800 dark:text-green-200',
    },
    disconnected: {
      icon: WifiOffIcon,
      iconClass: 'text-red-600',
      bgClass: 'bg-red-50 border-red-200 dark:bg-red-950 dark:border-red-800',
      text: 'Disconnected',
      textClass: 'text-red-800 dark:text-red-200',
    },
    reconnecting: {
      icon: RefreshCwIcon,
      iconClass: 'animate-spin text-orange-600',
      bgClass: 'bg-orange-50 border-orange-200 dark:bg-orange-950 dark:border-orange-800',
      text: `Reconnecting... (Attempt ${reconnectAttempts})`,
      textClass: 'text-orange-800 dark:text-orange-200',
    },
  }

  const config = statusConfig[status]
  const Icon = config.icon

  return (
    <div
      className={cn(
        'flex items-center gap-2 px-3 py-1.5 rounded-md border text-sm transition-all',
        config.bgClass,
        className
      )}
      role="status"
      aria-live="polite"
    >
      <Icon className={cn('h-4 w-4', config.iconClass)} />
      <span className={config.textClass}>{config.text}</span>
      {!isConnected && status === 'disconnected' && (
        <Button variant="ghost" size="sm" onClick={reconnect} className="h-6 px-2 text-xs">
          Retry
        </Button>
      )}
    </div>
  )
}

/**
 * Minimal connection indicator for navbar/header
 */
export function ConnectionIndicator({ className }: { className?: string }) {
  const { status, isConnected } = useWebSocketStatus()
  const { reconnect } = useWebSocket()

  const statusConfig = {
    connecting: {
      color: 'bg-yellow-500',
      title: 'Connecting...',
    },
    connected: {
      color: 'bg-green-500',
      title: 'Connected',
    },
    disconnected: {
      color: 'bg-red-500',
      title: 'Disconnected - Click to reconnect',
    },
    reconnecting: {
      color: 'bg-orange-500 animate-pulse',
      title: 'Reconnecting...',
    },
  }

  const config = statusConfig[status]

  return (
    <button
      type="button"
      onClick={!isConnected ? reconnect : undefined}
      disabled={isConnected || status === 'reconnecting'}
      className={cn(
        'relative h-2.5 w-2.5 rounded-full transition-colors',
        config.color,
        !isConnected &&
          status !== 'reconnecting' &&
          'cursor-pointer hover:ring-2 hover:ring-offset-2',
        className
      )}
      title={config.title}
      aria-label={config.title}
    >
      <span className="sr-only">{config.title}</span>
    </button>
  )
}
